/**
 * FASE 24.3 — DAILY OPERATIONAL DIGEST
 *
 * Generates a short pastoral summary of what deserves attention today.
 * Feels like "things you might want to keep in mind" — not a dashboard.
 *
 * Max 4 items, calm language, never repetitive.
 */

import { getActiveWorkflows } from "./workflowState";
import { getWorkflowDefinition } from "./workflowSchemas";
import { computeFollowupItems } from "./followupEngine";
import { getPriorityWorkflows } from "./workflowPriorities";

/**
 * @typedef {Object} DigestItem
 * @property {"followup"|"workflow"|"attention"|"progress"} kind
 * @property {string} text
 * @property {string|null} workflowId
 * @property {string|null} entityName
 */

/* ─── Phrasing ───────────────────────────────────────────────── */

const FOLLOWUP_PHRASES = [
  (name) => `${name} lleva un tiempo sin actividad.`,
  (name) => `Podría ser buen momento para retomar el contacto con ${name}.`,
  (name) => `${name} espera seguimiento.`,
];

const WORKFLOW_PHRASES = [
  (label, entity) => entity ? `${label} con ${entity} está avanzando.` : `${label} sigue en marcha.`,
  (label, entity) => entity ? `${label} — ${entity} aún en proceso.` : `${label} activo.`,
];

const ATTENTION_PHRASES = [
  (label) => `${label} necesita un poco de atención esta semana.`,
  (label) => `${label} lleva tiempo sin movimiento.`,
];

function pick(arr, seed) {
  return arr[seed % arr.length];
}

function seed(str) {
  let h = 0;
  for (let i = 0; i < Math.min(str?.length ?? 0, 8); i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}

/* ─── Session dedup ──────────────────────────────────────────── */
let _lastDigestTs = 0;
let _lastDigest   = null;
const DIGEST_CACHE_MS = 5 * 60 * 1000; // 5-min cache — avoid generating on every render

/**
 * Build a fresh operational digest, or return cached version.
 * @returns {DigestItem[]}
 */
export function buildOperationalDigest() {
  if (_lastDigest && Date.now() - _lastDigestTs < DIGEST_CACHE_MS) {
    return _lastDigest;
  }

  const items    = [];
  const seen     = new Set(); // deduplicate by workflowId

  /* ── Overdue followup items (highest priority) ──────────── */
  const allFollowups = computeFollowupItems();
  const followups = allFollowups.filter((i) => i.kind === "overdue" || i.kind === "no_progress");
  for (const item of followups) {
    if (items.length >= 2) break;
    if (seen.has(item.workflowId)) continue;
    seen.add(item.workflowId);
    const phrase = pick(FOLLOWUP_PHRASES, seed(item.workflowId));
    items.push({
      kind:       "followup",
      text:       phrase(item.entityName ?? "un proceso"),
      workflowId: item.workflowId,
      entityName: item.entityName,
    });
  }

  /* ── High-priority active workflows ────────────────────── */
  const topWfs = getPriorityWorkflows(3);
  for (const wf of topWfs) {
    if (items.length >= 4) break;
    if (seen.has(wf.id)) continue;
    seen.add(wf.id);
    const def        = getWorkflowDefinition(wf.type);
    const entityName = wf.related_entities?.[0]?.name;
    const s          = seed(wf.id);

    // Determine if this is a "needs attention" or "in progress" item
    const needsAttention = (wf._score ?? 0) >= 50 || allFollowups.some((i) => i.workflowId === wf.id);

    if (needsAttention) {
      items.push({
        kind:       "attention",
        text:       pick(ATTENTION_PHRASES, s)(entityName ? `${def?.label} con ${entityName}` : def?.label ?? wf.type),
        workflowId: wf.id,
        entityName,
      });
    } else if (wf.progress > 0 && wf.progress < 1) {
      items.push({
        kind:       "workflow",
        text:       pick(WORKFLOW_PHRASES, s)(def?.label ?? wf.type, entityName),
        workflowId: wf.id,
        entityName,
      });
    }
  }

  const digest = items.slice(0, 4);
  _lastDigest   = digest;
  _lastDigestTs = Date.now();
  return digest;
}

/** Invalidate the digest cache (call after workflow mutations). */
export function invalidateDigest() {
  _lastDigestTs = 0;
  _lastDigest   = null;
}

/** Build a compact digest text for prompt injection (max 200 chars). */
export function buildDigestPromptFragment() {
  const items = buildOperationalDigest();
  if (items.length === 0) return null;
  const lines = items.slice(0, 3).map((i) => `• ${i.text}`);
  const text  = `[hoy pastoral]\n${lines.join("\n")}`;
  return text.length > 200 ? text.slice(0, 197) + "…" : text;
}
